/**
 * Runtime resolver for the pipeline surface gate (SE-6257).
 *
 * `PIPELINE_SURFACE` (`@/config/featureFlags`) is a **build-time** const that
 * defaults `"off"`. Nothing a Playwright spec can do flips a build-time const,
 * so every pipeline acceptance criterion (SE-5487/5489/5490/5492/5504) was
 * unreachable from e2e — SE-5490 shipped with zero of five ACs verifiable.
 * This module makes the gate resolvable at **runtime** without changing what
 * production does.
 *
 * Precedence:
 *
 * 1. When overrides are allowed, a boolean at
 *    `globalThis[PIPELINE_SURFACE_OVERRIDE_KEY]` wins.
 * 2. Otherwise the build-time `PIPELINE_SURFACE` const decides, exactly as
 *    before.
 *
 * **Overrides are allowed only outside production.** The gate is
 * `env.EXPO_PUBLIC_ENV !== "production"`, the same environment gate the
 * `/playground` design-gallery route uses to keep a dev/QA surface out of
 * production builds (`src/app/(root)/playground/_layout.tsx`). A production
 * build bakes `EXPO_PUBLIC_ENV=production` from `.env.production`, so
 * `isOverrideAllowed` is constant-`false` there and the override branch can
 * never be taken — no matter what a page script writes to `globalThis`.
 *
 * Deliberately NOT gated on `__DEV__`: the Playwright web export is a
 * production-mode Metro build (`expo export --platform web`, no `--dev`), so
 * `__DEV__` is `false` in the very bundle the e2e suite runs against. That is
 * precisely why `PostHogBridge` had to fall back to `isAutomatedSession()` to
 * expose its own e2e bridge. Gating on `__DEV__` here would have compiled the
 * override out of the only build that needs it — a mechanism that looks like a
 * fix and changes nothing.
 *
 * Also deliberately NOT gated on `isAutomatedSession()` (`navigator.webdriver`):
 * that signal is present in a *production* deployment driven by any WebDriver,
 * which would make the surface externally settable in production.
 * @module config/pipelineSurface
 */
import { env } from "@/lib/env";

import { PIPELINE_SURFACE } from "./featureFlags";

/**
 * Global key an e2e spec writes (via `page.addInitScript`) to force the
 * pipeline surface on or off before the app boots.
 *
 * Mirrored verbatim in `e2e/fixtures/pipelineSurface.ts`. The e2e project has
 * no path alias into `src/`, so — exactly like `__posthogForE2E__`, which is
 * duplicated between `PostHogFeatureFlagsProvider.tsx` and
 * `e2e/fixtures/featureFlags.ts` — the string is repeated rather than imported.
 */
export const PIPELINE_SURFACE_OVERRIDE_KEY = "__pipelineSurfaceOverride__";

/**
 * Whether a runtime override may be consulted at all.
 * @returns `false` in a production build, `true` in every other environment
 * (localhost, dev, staging, and unset).
 */
const isOverrideAllowed = (): boolean => env.EXPO_PUBLIC_ENV !== "production";

/**
 * Resolves whether the pipeline surface is switched on.
 *
 * This is the single switch both pipeline read sites consult — the
 * `useIsPipelineSurfaceActive` hook (nav promotion + Scouting Boards-tab hide)
 * and `resolveKanbanSourceType` in the player-kanban `MainContainer`. Routing
 * only one of them through it would resolve a non-PIPELINE source type and
 * yield no pipeline board, defeating the purpose.
 * @returns `true` when the surface is on for this build/session.
 */
export const isPipelineSurfaceOn = (): boolean => {
  if (isOverrideAllowed()) {
    const override = Reflect.get(globalThis, PIPELINE_SURFACE_OVERRIDE_KEY);
    if (typeof override === "boolean") {
      return override;
    }
  }

  return PIPELINE_SURFACE === "on";
};
